import { cn } from "@/lib/utils";

type LoaderSize = "sm" | "md" | "lg";

type LoaderProps = {
  className?: string;
  size?: LoaderSize;
  text?: string;
  variant?:
    | "circular"
    | "classic"
    | "pulse"
    | "pulse-dot"
    | "dots"
    | "typing"
    | "wave"
    | "bars"
    | "terminal"
    | "text-blink"
    | "text-shimmer"
    | "loading-dots"
    | "braille";
};

function CircularLoader({
  className,
  size = "md",
}: {
  className?: string;
  size?: LoaderSize;
}) {
  const sizeClasses = { sm: "size-4", md: "size-5", lg: "size-6" };

  return (
    <div
      className={cn(
        "animate-spin rounded-full border-2 border-primary border-t-transparent",
        sizeClasses[size],
        className
      )}
    >
      <span className="sr-only">Loading</span>
    </div>
  );
}

const CLASSIC_MARGIN_LEFT: Record<LoaderSize, string> = {
  sm: "-0.75px",
  md: "-1px",
  lg: "-1.25px",
};

const CLASSIC_ORIGIN_X: Record<LoaderSize, string> = {
  sm: "0.75px",
  md: "1px",
  lg: "1.25px",
};

const CLASSIC_ORIGIN_Y: Record<LoaderSize, string> = {
  sm: "10px",
  md: "12px",
  lg: "14px",
};

function ClassicLoader({
  className,
  size = "md",
}: {
  className?: string;
  size?: LoaderSize;
}) {
  const sizeClasses = { sm: "size-4", md: "size-5", lg: "size-6" };
  const barSizes = {
    sm: { height: "6px", width: "1.5px" },
    md: { height: "8px", width: "2px" },
    lg: { height: "10px", width: "2.5px" },
  };

  return (
    <div className={cn("relative", sizeClasses[size], className)}>
      <div className="absolute h-full w-full">
        {Array.from({ length: 12 }, (_, i) => (
          <div
            className="absolute animate-[spinner-fade_1.2s_linear_infinite] rounded-full bg-primary"
            // biome-ignore lint/suspicious/noArrayIndexKey: static decorative elements
            key={i}
            style={{
              top: "0",
              left: "50%",
              marginLeft: CLASSIC_MARGIN_LEFT[size],
              transformOrigin: `${CLASSIC_ORIGIN_X[size]} ${CLASSIC_ORIGIN_Y[size]}`,
              transform: `rotate(${i * 30}deg)`,
              opacity: 0,
              animationDelay: `${i * 0.1}s`,
              height: barSizes[size].height,
              width: barSizes[size].width,
            }}
          />
        ))}
      </div>
      <span className="sr-only">Loading</span>
    </div>
  );
}

function PulseLoader({
  className,
  size = "md",
}: {
  className?: string;
  size?: LoaderSize;
}) {
  const sizeClasses = { sm: "size-4", md: "size-5", lg: "size-6" };

  return (
    <div className={cn("relative", sizeClasses[size], className)}>
      <div className="absolute inset-0 animate-[thin-pulse_1.5s_ease-in-out_infinite] rounded-full border-2 border-primary" />
      <span className="sr-only">Loading</span>
    </div>
  );
}

function PulseDotLoader({
  className,
  size = "md",
}: {
  className?: string;
  size?: LoaderSize;
}) {
  const sizeClasses = { sm: "size-1", md: "size-2", lg: "size-3" };

  return (
    <div
      className={cn(
        "animate-[pulse-dot_1.2s_ease-in-out_infinite] rounded-full bg-primary",
        sizeClasses[size],
        className
      )}
    >
      <span className="sr-only">Loading</span>
    </div>
  );
}

function DotsLoader({
  className,
  size = "md",
}: {
  className?: string;
  size?: LoaderSize;
}) {
  const dotSizes = { sm: "h-1.5 w-1.5", md: "h-2 w-2", lg: "h-2.5 w-2.5" };
  const containerSizes = { sm: "h-4", md: "h-5", lg: "h-6" };

  return (
    <div
      className={cn(
        "flex items-center space-x-1",
        containerSizes[size],
        className
      )}
    >
      {Array.from({ length: 3 }, (_, i) => (
        <div
          className={cn(
            "animate-[bounce-dots_1.4s_ease-in-out_infinite] rounded-full bg-primary",
            dotSizes[size]
          )}
          // biome-ignore lint/suspicious/noArrayIndexKey: static decorative elements
          key={i}
          style={{ animationDelay: `${i * 160}ms` }}
        />
      ))}
      <span className="sr-only">Loading</span>
    </div>
  );
}

function TypingLoader({
  className,
  size = "md",
}: {
  className?: string;
  size?: LoaderSize;
}) {
  const dotSizes = { sm: "h-1 w-1", md: "h-1.5 w-1.5", lg: "h-2 w-2" };
  const containerSizes = { sm: "h-4", md: "h-5", lg: "h-6" };

  return (
    <div
      className={cn(
        "flex items-center space-x-1",
        containerSizes[size],
        className
      )}
    >
      {Array.from({ length: 3 }, (_, i) => (
        <div
          className={cn(
            "animate-[typing_1s_infinite] rounded-full bg-primary",
            dotSizes[size]
          )}
          // biome-ignore lint/suspicious/noArrayIndexKey: static decorative elements
          key={i}
          style={{ animationDelay: `${i * 250}ms` }}
        />
      ))}
      <span className="sr-only">Loading</span>
    </div>
  );
}

function WaveLoader({
  className,
  size = "md",
}: {
  className?: string;
  size?: LoaderSize;
}) {
  const barWidths = { sm: "w-0.5", md: "w-0.5", lg: "w-1" };
  const containerSizes = { sm: "h-4", md: "h-5", lg: "h-6" };
  const heights = {
    sm: ["6px", "9px", "12px", "9px", "6px"],
    md: ["8px", "12px", "16px", "12px", "8px"],
    lg: ["10px", "15px", "20px", "15px", "10px"],
  };

  return (
    <div
      className={cn(
        "flex items-center gap-0.5",
        containerSizes[size],
        className
      )}
    >
      {Array.from({ length: 5 }, (_, i) => (
        <div
          className={cn(
            "animate-[wave_1s_ease-in-out_infinite] rounded-full bg-primary",
            barWidths[size]
          )}
          // biome-ignore lint/suspicious/noArrayIndexKey: static decorative elements
          key={i}
          style={{
            animationDelay: `${i * 100}ms`,
            height: heights[size][i],
          }}
        />
      ))}
      <span className="sr-only">Loading</span>
    </div>
  );
}

function BarsLoader({
  className,
  size = "md",
}: {
  className?: string;
  size?: LoaderSize;
}) {
  const barWidths = { sm: "w-1", md: "w-1.5", lg: "w-2" };
  const containerSizes = {
    sm: "h-4 gap-1",
    md: "h-5 gap-1.5",
    lg: "h-6 gap-2",
  };

  return (
    <div className={cn("flex", containerSizes[size], className)}>
      {Array.from({ length: 3 }, (_, i) => (
        <div
          className={cn(
            "h-full animate-[wave-bars_1.2s_ease-in-out_infinite] bg-primary",
            barWidths[size]
          )}
          // biome-ignore lint/suspicious/noArrayIndexKey: static decorative elements
          key={i}
          style={{ animationDelay: `${i * 0.2}s` }}
        />
      ))}
      <span className="sr-only">Loading</span>
    </div>
  );
}

function TerminalLoader({
  className,
  size = "md",
}: {
  className?: string;
  size?: LoaderSize;
}) {
  const cursorSizes = { sm: "h-3 w-1.5", md: "h-4 w-2", lg: "h-5 w-2.5" };
  const textSizes = { sm: "text-xs", md: "text-sm", lg: "text-base" };
  const containerSizes = { sm: "h-4", md: "h-5", lg: "h-6" };

  return (
    <div
      className={cn(
        "flex items-center space-x-1",
        containerSizes[size],
        className
      )}
    >
      <span className={cn("font-mono text-primary", textSizes[size])}>
        {">"}
      </span>
      <div
        className={cn(
          "animate-[blink_1s_step-end_infinite] bg-primary",
          cursorSizes[size]
        )}
      />
      <span className="sr-only">Loading</span>
    </div>
  );
}

function TextBlinkLoader({
  text = "Thinking",
  className,
  size = "md",
}: {
  text?: string;
  className?: string;
  size?: LoaderSize;
}) {
  const textSizes = { sm: "text-xs", md: "text-sm", lg: "text-base" };

  return (
    <div
      className={cn(
        "animate-[text-blink_2s_ease-in-out_infinite] font-medium",
        textSizes[size],
        className
      )}
    >
      {text}
    </div>
  );
}

function TextShimmerLoader({
  text = "Thinking",
  className,
  size = "md",
}: {
  text?: string;
  className?: string;
  size?: LoaderSize;
}) {
  const textSizes = { sm: "text-xs", md: "text-sm", lg: "text-base" };

  return (
    <div
      className={cn(
        "bg-[linear-gradient(to_right,var(--muted-foreground)_40%,var(--foreground)_60%,var(--muted-foreground)_80%)]",
        "bg-size-[200%_auto] bg-clip-text font-medium text-transparent",
        "animate-[shimmer_4s_infinite_linear]",
        textSizes[size],
        className
      )}
    >
      {text}
    </div>
  );
}

function TextDotsLoader({
  className,
  text = "Thinking",
  size = "md",
}: {
  className?: string;
  text?: string;
  size?: LoaderSize;
}) {
  const textSizes = { sm: "text-xs", md: "text-sm", lg: "text-base" };

  return (
    <div className={cn("inline-flex items-center", className)}>
      <span
        className={cn("font-medium text-muted-foreground", textSizes[size])}
      >
        {text}
      </span>
      <span className="inline-flex">
        <span className="animate-[loading-dots_1.4s_infinite_0.2s] text-muted-foreground">
          .
        </span>
        <span className="animate-[loading-dots_1.4s_infinite_0.4s] text-muted-foreground">
          .
        </span>
        <span className="animate-[loading-dots_1.4s_infinite_0.6s] text-muted-foreground">
          .
        </span>
      </span>
    </div>
  );
}

function BrailleLoader({
  className,
  size = "md",
  text,
}: {
  className?: string;
  size?: LoaderSize;
  text?: string;
}) {
  const spinnerSizes = { sm: "text-sm", md: "text-base", lg: "text-lg" };
  const labelSizes = { sm: "text-xs", md: "text-sm", lg: "text-base" };

  return (
    <div className={cn("inline-flex items-center gap-1.5", className)}>
      <span
        aria-hidden="true"
        className={cn(
          "before:animate-[braille-spin_0.8s_steps(10,end)_infinite] before:content-['⠋']",
          "font-mono text-primary leading-none",
          spinnerSizes[size]
        )}
      />
      {text && (
        <span
          className={cn("text-muted-foreground leading-none", labelSizes[size])}
        >
          {text}
        </span>
      )}
      {!text && <span className="sr-only">Loading</span>}
    </div>
  );
}

function Loader({
  variant = "circular",
  size = "md",
  text,
  className,
}: LoaderProps) {
  switch (variant) {
    case "circular":
      return <CircularLoader className={className} size={size} />;
    case "classic":
      return <ClassicLoader className={className} size={size} />;
    case "pulse":
      return <PulseLoader className={className} size={size} />;
    case "pulse-dot":
      return <PulseDotLoader className={className} size={size} />;
    case "dots":
      return <DotsLoader className={className} size={size} />;
    case "typing":
      return <TypingLoader className={className} size={size} />;
    case "wave":
      return <WaveLoader className={className} size={size} />;
    case "bars":
      return <BarsLoader className={className} size={size} />;
    case "terminal":
      return <TerminalLoader className={className} size={size} />;
    case "text-blink":
      return <TextBlinkLoader className={className} size={size} text={text} />;
    case "text-shimmer":
      return (
        <TextShimmerLoader className={className} size={size} text={text} />
      );
    case "loading-dots":
      return <TextDotsLoader className={className} size={size} text={text} />;
    case "braille":
      return <BrailleLoader className={className} size={size} text={text} />;
    default:
      return <CircularLoader className={className} size={size} />;
  }
}

export type { LoaderProps };
export { Loader };
